// The window manager store. One record per open window (apps are singletons,
// so the window id is the app id). Components subscribe to their own record
// with selectors, so dragging one window never re-renders the desktop.

import { create } from 'zustand';
import { local } from '../lib/storage';
import { APPS, type AppId } from './apps';
import { clampBounds, defaultBounds, fitBounds, type Area, type Bounds } from './geometry';
import { activeProcessesFor } from './processes';
import { hasUnsavedChanges } from './unsaved';

export interface Win {
  id: AppId;
  bounds: Bounds; // current floating bounds (ignored while maximized)
  z: number;
  minimized: boolean;
  maximized: boolean;
  phase: 'open' | 'closing';
  arg: Record<string, unknown> | null;
  argSeq: number; // bumps every time open() passes a new argument
  confirmClose: boolean; // showing the "work is running" dialog
  openSeq: number;
}

interface Layout {
  bounds: Partial<Record<AppId, Bounds>>;
  maximized: Partial<Record<AppId, boolean>>;
}

interface WmState {
  wins: Partial<Record<AppId, Win>>;
  order: AppId[]; // taskbar order (open order)
  focused: AppId | null;
  area: Area;
  phone: boolean;
  open: (id: AppId, arg?: Record<string, unknown> | null) => void;
  focus: (id: AppId) => void;
  minimize: (id: AppId) => void;
  restore: (id: AppId) => void;
  taskbarToggle: (id: AppId) => void;
  toggleMaximize: (id: AppId) => void;
  setBounds: (id: AppId, b: Bounds) => void;
  requestClose: (id: AppId) => void;
  cancelClose: (id: AppId) => void;
  close: (id: AppId) => void;
  remove: (id: AppId) => void;
  closeAll: () => void;
  setArea: (a: Area) => void;
  setPhone: (p: boolean) => void;
}

let zTop = 10;
let cascade = 0;
let openSeq = 0;
const layout: Layout = local.get<Layout>('layout.v1', { bounds: {}, maximized: {} });
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function persist(state: Pick<WmState, 'wins' | 'order'>) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    for (const w of Object.values(state.wins)) {
      if (!w) continue;
      layout.bounds[w.id] = w.bounds;
      layout.maximized[w.id] = w.maximized;
    }
    local.set('layout.v1', layout);
    local.set(
      'session.v1',
      state.order.filter((id) => state.wins[id]?.phase === 'open').map((id) => ({ id, min: !!state.wins[id]?.minimized })),
    );
  }, 250);
}

function topVisible(wins: WmState['wins'], except?: AppId): AppId | null {
  let best: Win | null = null;
  for (const w of Object.values(wins)) {
    if (!w || w.id === except || w.minimized || w.phase !== 'open') continue;
    if (!best || w.z > best.z) best = w;
  }
  return best?.id ?? null;
}

export const useWm = create<WmState>((set, get) => {
  const update = (fn: (s: WmState) => Partial<WmState>) => {
    set(fn);
    persist(get());
  };
  const patchWin = (id: AppId, p: Partial<Win>) => (s: WmState) => {
    const w = s.wins[id];
    return w ? { wins: { ...s.wins, [id]: { ...w, ...p } } } : {};
  };

  return {
    wins: {},
    order: [],
    focused: null,
    area: { w: typeof innerWidth === 'number' ? innerWidth : 1280, h: typeof innerHeight === 'number' ? innerHeight - 50 : 720 },
    phone: false,

    open: (id, arg = null) => {
      const s = get();
      const existing = s.wins[id];
      if (existing && existing.phase === 'open') {
        update((st) => ({
          ...patchWin(id, {
            minimized: false,
            z: ++zTop,
            arg: arg ?? existing.arg,
            argSeq: arg ? existing.argSeq + 1 : existing.argSeq,
          })(st),
          focused: id,
        }));
        return;
      }
      const meta = APPS[id];
      const saved = layout.bounds[id];
      const bounds = saved
        ? fitBounds(saved, s.area, meta.minW, meta.minH)
        : defaultBounds(meta.w, meta.h, s.area, cascade++, 220);
      const win: Win = {
        id,
        bounds,
        z: ++zTop,
        minimized: false,
        maximized: !!layout.maximized[id],
        phase: 'open',
        arg,
        argSeq: arg ? 1 : 0,
        confirmClose: false,
        openSeq: ++openSeq,
      };
      update((st) => ({
        wins: { ...st.wins, [id]: win },
        order: [...st.order.filter((o) => o !== id), id],
        focused: id,
      }));
    },

    focus: (id) => {
      const s = get();
      const w = s.wins[id];
      if (!w || w.phase !== 'open') return;
      if (s.focused === id && w.z === zTop && !w.minimized) return;
      set((st) => ({ ...patchWin(id, { z: ++zTop, minimized: false })(st), focused: id }));
    },

    minimize: (id) =>
      update((st) => ({
        ...patchWin(id, { minimized: true })(st),
        focused: st.focused === id ? topVisible(st.wins, id) : st.focused,
      })),

    restore: (id) => update((st) => ({ ...patchWin(id, { minimized: false, z: ++zTop })(st), focused: id })),

    taskbarToggle: (id) => {
      const s = get();
      const w = s.wins[id];
      if (!w) return get().open(id);
      if (w.minimized) return get().restore(id);
      if (s.focused === id) return get().minimize(id);
      get().focus(id);
    },

    toggleMaximize: (id) => update((st) => patchWin(id, { maximized: !st.wins[id]?.maximized, z: ++zTop })(st)),

    setBounds: (id, b) => {
      const meta = APPS[id];
      update((st) => patchWin(id, { bounds: clampBounds(b, st.area, meta.minW, meta.minH), maximized: false })(st));
    },

    requestClose: (id) => {
      const w = get().wins[id];
      if (!w || w.phase !== 'open') return;
      if (activeProcessesFor(id).length || hasUnsavedChanges(id)) {
        set((st) => ({ ...patchWin(id, { confirmClose: true, minimized: false, z: ++zTop })(st), focused: id }));
        return;
      }
      get().close(id);
    },

    cancelClose: (id) => set(patchWin(id, { confirmClose: false })),

    close: (id) =>
      update((st) => ({
        ...patchWin(id, { phase: 'closing', confirmClose: false })(st),
        focused: st.focused === id ? topVisible(st.wins, id) : st.focused,
        order: st.order.filter((o) => o !== id),
      })),

    remove: (id) =>
      update((st) => {
        const wins = { ...st.wins };
        if (wins[id]?.phase === 'closing') delete wins[id];
        return { wins };
      }),

    closeAll: () => update(() => ({ wins: {}, order: [], focused: null })),

    setArea: (area) =>
      set((st) => {
        const wins = { ...st.wins };
        for (const w of Object.values(wins)) {
          if (!w) continue;
          const meta = APPS[w.id];
          wins[w.id] = { ...w, bounds: fitBounds(w.bounds, area, meta.minW, meta.minH) };
        }
        return { area, wins };
      }),

    setPhone: (phone) => set({ phone }),
  };
});

export const wm = () => useWm.getState();
export const openApp = (id: AppId, arg?: Record<string, unknown> | null) => useWm.getState().open(id, arg ?? null);

/** Windows that were open when the tab was last closed (restored after boot). */
export function savedSession(): { id: AppId; min: boolean }[] {
  const raw = local.get<{ id: string; min: boolean }[]>('session.v1', []);
  return Array.isArray(raw) ? raw.filter((r) => r && r.id in APPS).map((r) => ({ id: r.id as AppId, min: !!r.min })) : [];
}

export function resetLayout() {
  layout.bounds = {};
  layout.maximized = {};
  local.del('layout.v1');
  local.del('session.v1');
  local.del('icons.v1');
}
